import { useEffect, useRef, useState } from 'react'
import { canSpeak, isMuted, onMuteChange, setMuted } from '../lib/speech.js'
import { track } from '../lib/analytics.js'
import Wordmark from './Wordmark.jsx'

/**
 * Small, out-of-the-way chrome: app name, sound toggle, collection, and the age
 * toggle (SPEC screen 1: "then a small toggle in a corner").
 *
 * The age toggle is press-and-hold: a 2-year-old mashes corners, and changing
 * age mid-session throws away the story (D-056).
 *
 * Since the app opens at age 6 instead of asking (D-133) this chip is the only
 * road to the picker, so a plain tap can no longer do nothing at all: it says
 * "hold to change" for a moment and leaves the age alone. The keyboard opens it
 * on Enter — a hold is a pointer gesture, and nothing about a keyboard needs
 * protecting from a toddler.
 */
export default function TopBar({ age, screen, onAge, onHome, onCollection }) {
  const [muted, setMutedState] = useState(isMuted())
  const [holding, setHolding] = useState(false)
  const [hint, setHint] = useState(false)
  const holdTimer = useRef(null)
  const hintTimer = useRef(null)
  const opened = useRef(false)
  useEffect(() => onMuteChange(setMutedState), [])
  useEffect(() => () => { clearTimeout(holdTimer.current); clearTimeout(hintTimer.current) }, [])

  const startHold = () => {
    setHolding(true)
    setHint(false)
    opened.current = false
    clearTimeout(holdTimer.current)
    clearTimeout(hintTimer.current)
    holdTimer.current = setTimeout(() => {
      setHolding(false)
      opened.current = true
      onAge()
    }, 700)
  }
  const endHold = () => {
    setHolding(false)
    clearTimeout(holdTimer.current)
    // Let go early and the chip explains itself instead of doing nothing.
    if (!opened.current) {
      setHint(true)
      clearTimeout(hintTimer.current)
      hintTimer.current = setTimeout(() => setHint(false), 1800)
    }
  }

  return (
    <header className="topbar">
      <button className="topbar__home" onClick={onHome} aria-label="Home">
        <img className="topbar__mark" src="/icons/icon.svg" alt="" aria-hidden="true" width="36" height="36" />
        <Wordmark className="topbar__wordmark" aria-hidden="true" />
      </button>
      <div className="topbar__spacer" />
      {canSpeak() && (
        <button className="topbar__btn" onClick={() => { track('mute', { on: !muted }); setMuted(!muted) }} aria-label={muted ? 'Turn sound on' : 'Turn sound off'} title={muted ? 'Sound is off' : 'Sound is on'}>
          {muted ? '🔇' : '🔊'}
        </button>
      )}
      {screen !== 'collection' && (
        <button className="topbar__btn" onClick={onCollection} aria-label="My collection">
          🏆
        </button>
      )}
      {age && (
        <span className="topbar__agewrap">
          <button
            className={`topbar__age${holding ? ' is-holding' : ''}`}
            onPointerDown={startHold}
            onPointerUp={endHold}
            onPointerLeave={endHold}
            onPointerCancel={endHold}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); opened.current = true; onAge() } }}
            onContextMenu={(e) => e.preventDefault()}
            aria-label={`Age ${age}. Hold to change.`}
            title="Hold to change age"
          >
            age {age}
          </button>
          {hint && <span className="topbar__hint" role="status">Hold to change age</span>}
        </span>
      )}
    </header>
  )
}
